import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  UserGroupIcon,
  UsersIcon,
  ExclamationCircleIcon,
  EyeIcon,
  PencilIcon,
  CalendarIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserPlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import AddPatientModal from './AddPatientModal';
import ViewPatientModal from './ViewPatientModal';
import { patientService, Patient, CreatePatientRequest, UpdatePatientRequest } from '../../services/patientService';

interface PatientsClientProps {
  initialPatients: Patient[];
}

/** Status badge class map — fully theme-aware with light/dark variants */
const STATUS_CLASSES: Record<string, string> = {
  active: 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-700/30',
  new:    'bg-sky-100 dark:bg-sky-900/20 text-sky-700 dark:text-sky-400 border-sky-200 dark:border-sky-700/30',
  inactive: 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-700/30',
};

/** Statistics card config — avoids duplicated markup */
const STAT_ICONS = {
  total:    { Icon: UsersIcon,              color: 'bg-primary/10 text-primary' },
  active:   { Icon: CheckCircleIcon,        color: 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400' },
  new:      { Icon: UserPlusIcon,           color: 'bg-sky-100 dark:bg-sky-900/20 text-sky-600 dark:text-sky-400' },
  inactive: { Icon: ExclamationCircleIcon,  color: 'bg-amber-100 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400' },
} as const;

export default function PatientsClient({ initialPatients }: PatientsClientProps) {
  const { t } = useTranslation(['patients', 'common']);
  const navigate = useNavigate();
  const [patients, setPatients] = useState<Patient[]>(initialPatients);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [viewPatient, setViewPatient] = useState<Patient | null>(null);

  // Memoized statistics
  const stats = useMemo(() => {
    const total = patients.length;
    const active = patients.filter(p => p.status === 'active').length;
    const newP = patients.filter(p => p.status === 'new').length;
    const inactive = patients.filter(p => p.status === 'inactive').length;
    return [
      { key: 'total' as const,    value: total,    label: t('stats.total') },
      { key: 'active' as const,   value: active,   label: t('stats.active') },
      { key: 'new' as const,      value: newP,     label: t('stats.new') },
      { key: 'inactive' as const, value: inactive,  label: t('stats.inactive') },
    ];
  }, [patients, t]);

  // Memoized filter
  const filteredPatients = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return patients.filter(patient => {
      const matchesSearch = !term || (
        patient.firstName.toLowerCase().includes(term) ||
        patient.lastName.toLowerCase().includes(term) ||
        patient.email.toLowerCase().includes(term) ||
        patient.phone.includes(searchTerm)
      );
      const matchesStatus = statusFilter === 'all' || patient.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [patients, searchTerm, statusFilter]);

  const getInitials = (firstName: string, lastName: string) =>
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

  // Handle patient save (add or edit)
  const handleSavePatient = async (patientData: Partial<Patient>) => {
    try {
      if (selectedPatient) {
        const updateData: UpdatePatientRequest = {
          first_name: patientData.firstName || selectedPatient.firstName,
          last_name: patientData.lastName || selectedPatient.lastName,
          email: patientData.email || selectedPatient.email,
          phone_number: patientData.phone || selectedPatient.phone,
          gender: patientData.gender || selectedPatient.gender,
          date_of_birth: patientData.birthDate || selectedPatient.birthDate,
          address: patientData.address || selectedPatient.address,
          insurance: patientData.insurance ?? selectedPatient.insurance,
          whatsapp_phone: patientData.whatsappPhone,
          profession: patientData.profession,
          cin_number: patientData.cinNumber,
          referred_by: patientData.referredBy,
          medical_history_allergies: patientData.medicalHistoryAllergies ?? selectedPatient.medical_history_allergies,
          medical_history_diseases: patientData.medicalHistoryDiseases ?? selectedPatient.medical_history_diseases,
          medical_history_medications: patientData.medicalHistoryMedications ?? selectedPatient.medical_history_medications,
        };
        
        const response = await patientService.updatePatient(selectedPatient.id, updateData);
        
        if (response.success) {
          setPatients(prev => prev.map(p => 
            p.id === selectedPatient.id ? { ...p, ...patientData } : p
          ));
        } else {
          alert(response.message || 'Failed to update patient');
          return;
        }
      } else {
        const createData: CreatePatientRequest = {
          first_name: patientData.firstName || '',
          last_name: patientData.lastName || '',
          email: patientData.email || '',
          phone_number: patientData.phone || '',
          gender: patientData.gender || '',
          date_of_birth: patientData.birthDate || '',
          address: patientData.address || '',
          insurance: patientData.insurance,
          whatsapp_phone: patientData.whatsappPhone,
          profession: patientData.profession,
          cin_number: patientData.cinNumber,
          referred_by: patientData.referredBy,
          medical_history_allergies: patientData.medicalHistoryAllergies,
          medical_history_diseases: patientData.medicalHistoryDiseases,
          medical_history_medications: patientData.medicalHistoryMedications,
        };
        
        const response = await patientService.createPatient(createData);
        
        if (response.success) {
          const newPatient: Patient = {
            id: response.patient_id || `patient-${Date.now()}`,
            firstName: patientData.firstName || '',
            lastName: patientData.lastName || '',
            email: patientData.email || '',
            phone: patientData.phone || '',
            gender: patientData.gender || '',
            birthDate: patientData.birthDate || '',
            address: patientData.address || '',
            dateOfBirth: patientData.birthDate || '',
            lastVisit: new Date().toISOString(),
            status: 'new',
            medicalConditions: [],
            insurance: '',
            totalVisits: 0
          };
          setPatients(prev => [...prev, newPatient]);
        } else {
          alert(response.message || 'Failed to create patient');
          return;
        }
      }
      
      setShowAddModal(false);
      setSelectedPatient(null);
    } catch (error) {
      console.error('Error saving patient:', error);
      alert('Error saving patient: ' + error);
    }
  };

  const handleEditPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    setShowAddModal(true);
  };

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="relative overflow-hidden rounded-2xl p-8 border border-border bg-gradient-to-br from-emerald-500/[0.07] to-indigo-500/[0.07] dark:from-emerald-500/[0.04] dark:to-indigo-500/[0.04] backdrop-blur-xl shadow-sm">
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-1">
              {t('title')}
            </h1>
            <p className="text-base text-muted-foreground">
              {t('subtitle')}
            </p>
            <div className="mt-5">
              <button 
                className="btn-primary"
                onClick={() => { setSelectedPatient(null); setShowAddModal(true); }}
              >
                <UserPlusIcon className="h-5 w-5" />
                <span>{t('actions.addPatient')}</span>
              </button>
            </div>
          </div>
          <div className="hidden lg:flex h-20 w-20 rounded-2xl items-center justify-center bg-gradient-to-br from-emerald-500 to-indigo-500 shadow-lg shadow-emerald-500/20 dark:shadow-emerald-500/10 border border-white/20">
            <UserGroupIcon className="h-10 w-10 text-white drop-shadow-sm" />
          </div>
        </div>

        {/* Decorative blurs */}
        <div className="absolute -top-4 -right-4 h-32 w-32 rounded-full bg-emerald-500/10 blur-2xl pointer-events-none" />
        <div className="absolute -bottom-4 -left-4 h-24 w-24 rounded-full bg-indigo-500/10 blur-xl pointer-events-none" />
      </div>

      {/* ── Statistics Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ key, value, label }) => {
          const { Icon, color } = STAT_ICONS[key];
          return (
            <div
              key={key}
              className="bg-card border border-border rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-card-foreground">{value}</p>
                  <p className="text-sm font-medium text-muted-foreground">{label}</p>
                </div>
                <div className={`p-3 rounded-xl ${color}`}>
                  <Icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Search & Filter ── */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-muted-foreground" />
            </div>
            <input
              type="text"
              placeholder={t('search.placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2.5 w-full bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-muted-foreground" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-background border border-border rounded-lg px-3 py-2.5 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
            >
              <option value="all">{t('filters.allStatus')}</option>
              <option value="active">{t('common:status.active')}</option>
              <option value="new">{t('common:status.new')}</option>
              <option value="inactive">{t('common:status.inactive')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* ── Patients Table ── */}
      <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                {['table.patient', 'table.contact', 'table.lastVisit', 'table.status', 'table.visits', 'table.actions'].map(col => (
                  <th key={col} className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t(col)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredPatients.map((patient) => (
                <tr 
                  key={patient.id}
                  className="hover:bg-muted/40 transition-colors duration-150"
                >
                  {/* Patient name + avatar */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-primary/10 dark:bg-primary/15 flex items-center justify-center text-sm font-semibold text-primary border border-primary/20">
                        {getInitials(patient.firstName, patient.lastName)}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground">
                          {patient.firstName} {patient.lastName}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          ID: {patient.id.slice(-8)}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Contact */}
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5">
                        <EnvelopeIcon className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-sm text-foreground">{patient.email}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <PhoneIcon className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">{patient.phone}</span>
                      </div>
                    </div>
                  </td>

                  {/* Last visit */}
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-foreground">
                      {new Date(patient.lastVisit).toLocaleDateString()}
                    </div>
                    {patient.nextAppointment && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Next: {new Date(patient.nextAppointment).toLocaleDateString()}
                      </div>
                    )}
                  </td>

                  {/* Status badge */}
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_CLASSES[patient.status] || ''}`}>
                      {patient.status === 'active' && <CheckCircleIcon className="h-3 w-3 mr-1" />}
                      {t(`common:status.${patient.status}`)}
                    </span>
                  </td>

                  {/* Visits */}
                  <td className="px-6 py-4">
                    <span className="text-sm font-semibold text-foreground">{patient.totalVisits}</span>
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      <button 
                        className="p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                        title={t('actions.viewPatient')}
                        onClick={() => setViewPatient(patient)}
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button 
                        className="p-2 rounded-lg text-muted-foreground hover:text-amber-500 hover:bg-amber-500/10 transition-colors"
                        onClick={() => handleEditPatient(patient)}
                        title={t('actions.editPatient')}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button 
                        className="p-2 rounded-lg text-muted-foreground hover:text-emerald-500 hover:bg-emerald-500/10 transition-colors"
                        title={t('actions.scheduleAppointment')}
                        onClick={() => navigate('/appointments', { state: { selectPatient: { id: patient.id, firstName: patient.firstName, lastName: patient.lastName } } })}
                      >
                        <CalendarIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Empty State ── */}
      {filteredPatients.length === 0 && (
        <div className="text-center py-16 rounded-xl border border-border bg-card">
          <UserGroupIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
          <h3 className="text-lg font-medium text-foreground mb-1">{t('noPatients')}</h3>
          <p className="text-muted-foreground text-sm">{t('adjustFilters')}</p>
        </div>
      )}

      {/* ── Add/Edit Patient Modal ── */}
      <AddPatientModal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setSelectedPatient(null); }}
        selectedPatient={selectedPatient}
        onSave={handleSavePatient}
      />

      {/* ── View Patient Modal ── */}
      <ViewPatientModal
        isOpen={!!viewPatient}
        onClose={() => setViewPatient(null)}
        patient={viewPatient}
        onEdit={(p) => { setViewPatient(null); handleEditPatient(p); }}
      />
    </div>
  );
}
